import { MIME, downloadBase64 } from '@shared/utils/download';

// MIME 'application/pdf' здесь корректен — заменён на общую константу MIME.PDF
export const openBase64PdfInNewWindow = (base64: string) => {
  const blob = new Blob([Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))], { type: MIME.PDF });
  window.open(URL.createObjectURL(blob));
};

/**
 * OSI-763: обобщённая выгрузка base64 в файл — понадобилась для CMS-контейнера
 * подписанного акта (`application/pkcs7-mime`, расширение `.cms`).
 *
 * Слияние с OSI-800: внутри намеренно НЕ `data:`-URL, а общий `downloadBase64`
 * (blob:-URL, якорь в DOM, отложенный revoke) — iOS/WebKit не скачивает data:-URL
 * по атрибуту download и режет его по длине.
 */
export const downloadBase64File = (base64: string, fileName: string, mimeType: string) =>
  downloadBase64(base64, mimeType, fileName);

// iOS/WebKit: data:-URL не скачивается по атрибуту download — переведено на blob: через downloadBase64
export const downloadBase64Pdf = (base64: string, fileName: string) => downloadBase64File(base64, fileName, MIME.PDF);

/** Сигнатура начала PDF-документа. PDF-ридеры ищут её в первых 1024 байтах файла. */
const PDF_HEADER = '%PDF-';

/** Маркер конца PDF-документа: последняя значащая лексема файла по спецификации PDF. */
const PDF_EOF = '%%EOF';

/**
 * OSI-763: извлечение подписанного PDF из CMS-контейнера ЭЦП (для ПРОСМОТРА).
 *
 * ЗАЧЕМ. Скан подписанного акта на бэкенде лежит не как PDF, а как CMS/CAdES-контейнер
 * (`.cms`): подписанный документ обёрнут в DER-структуру SignedData вместе с сертификатами
 * и метками времени. Если такой файл отдать в `PdfViewer` (react-pdf 7.6 / pdfjs-dist
 * 3.11.174), pdf.js не просто показывает пустую область — он ВЕШАЕТ РЕНДЕРЕР вкладки.
 * Воспроизведено в браузере на реальном акте 22936 (тест, ОСИ 1318): область просмотра
 * пустая, вкладка подвисает, снятие скриншота дважды отвалилось по CDP-таймауту
 * «renderer may be frozen». Это факт, а не гипотеза.
 *
 * СТРУКТУРА ФАЙЛА (снята через fetch в браузере, акт 22936):
 *   scan.fileName:            act_0000027653_31-08-2026.cms
 *   всего байт:               39355
 *   первые 16 байт:           30 82 99 b6 06 09 2a 86 48 86 f7 0d 01 07 02 a0
 *                             (DER SEQUENCE + OID signedData)
 *   '%PDF-' на смещении:      65
 *   последний '%%EOF' на:     34303
 *   хвост после PDF:          5047 байт (сертификаты, signerInfos, TSP)
 *
 * ЧТО ДЕЛАЕТ. Отрезает DER-префикс и DER-хвост, оставляя байты от `%PDF-` до конца
 * последнего `%%EOF`. Это ровно тот документ, который был подписан, то есть оригинал
 * по смыслу — контейнер подписи не меняет содержимое, а оборачивает его.
 *
 * СКАЧИВАНИЕ ЭТИМ НЕ ПОЛЬЗУЕТСЯ. Кнопка «Сохранить» (`onDownloadAct` в
 * `widgets/osiActs/model/viewModel.ts`) грузит скан отдельным запросом
 * `GET /core/api/Scans/{id}` и отдаёт ПОЛНЫЙ контейнер байт-в-байт: юридическую силу
 * имеет он, а не извлечённая часть. Функция применяется ТОЛЬКО к данным, которые идут
 * в область просмотра (`signedActsDialog.doc.base64`).
 *
 * ГРАНИЦА СПРАВА. Режем строго по концу `%%EOF`, БЕЗ завершающего перевода строки.
 * По спецификации PDF `%%EOF` — последняя значащая лексема, а EOL после неё
 * необязателен и ридерами игнорируется. Захватывать «ещё один байт на всякий случай»
 * нельзя: сразу за PDF начинается DER-хвост с произвольными байтами, и первый из них
 * вполне может оказаться 0x0A/0x0D — тогда мы бы дописали в документ мусор вместо EOL.
 *
 * БИНАРНАЯ БЕЗОПАСНОСТЬ. Работаем на строке из `atob` (коды символов 0..255, один
 * символ = один байт): посимвольные `indexOf`/`lastIndexOf`/`slice` по ней корректны.
 * TextDecoder и любые перекодировки здесь применять НЕЛЬЗЯ — они побьют бинарные байты.
 *
 * @param base64 содержимое скана как есть с бэкенда
 * @returns base64 извлечённого PDF либо исходную строку БЕЗ ИЗМЕНЕНИЙ, если извлекать
 *          нечего (файл уже чистый PDF, сигнатуры нет вовсе или это не base64)
 */
export const extractPdfFromCms = (base64: string): string => {
  if (!base64) return base64;

  let binary: string;

  try {
    binary = atob(base64);
  } catch {
    // Не base64 — ломать вход не имеем права, отдаём как пришло.
    return base64;
  }

  const start = binary.indexOf(PDF_HEADER);

  // start === -1 — сигнатуры нет вовсе (не PDF и не контейнер с PDF внутри);
  // start === 0  — это уже чистый PDF, резать нечего.
  // В обоих случаях возвращаем ИСХОДНУЮ строку, а не перекодированную: так вызывающий
  // код гарантированно получает байт-в-байт то же, что пришло с бэкенда.
  if (start <= 0) return base64;

  const eofIndex = binary.lastIndexOf(PDF_EOF);

  // `%%EOF` не найден (обрезанный или нестандартный PDF) — отрезаем только префикс
  // и берём всё до конца файла: хвост подписи мешает меньше, чем DER-заголовок.
  const end = eofIndex === -1 ? binary.length : eofIndex + PDF_EOF.length;

  return btoa(binary.slice(start, end));
};
